import React, { FC, lazy } from 'react'
import { RouteObject } from 'react-router'
import { Navigate, useRoutes } from 'react-router-dom'
import { WrapperRouteComponent, WrapperRouteWithOutLayoutComponent } from './config'
import LoginPage from '@pages/login'
import LayoutPage from '@pages/layout'
import Empty from '@components/empty'
import { useIntl } from 'react-intl'

// 工作台
const DashboardWorkbench = lazy(() => import('@src/pages/dashboard/workbench'))
// 分析页
const DashboardAnalysis = lazy(() => import('@src/pages/dashboard/analysis'))

// 基础表单
const FormBasic = lazy(() => import('@src/pages/form/basic'))
// 高级表单
const FormAdvanced = lazy(() => import('@src/pages/form/advanced'))
const FormEditor = lazy(() => import('@src/pages/form/editor'))
// 标准列表页
const ListSearch = lazy(() => import('@src/pages/list/search'))
// 结果页
const ResultSuccess = lazy(() => import('@src/pages/result/success'))
const ResultFailed = lazy(() => import('@src/pages/result/failed'))

// 异常页面
const Abnormal403 = lazy(() => import('@src/pages/abnormal/403'))
const Abnormal404 = lazy(() => import('@src/pages/abnormal/404'))
const Abnormal500 = lazy(() => import('@src/pages/abnormal/500'))
// 个人
const UserCenter = lazy(() => import('@src/pages/user/center'))
const UserSettings = lazy(() => import('@src/pages/user/settings'))

// 组织
const OrgDept = lazy(() => import('@src/pages/org/dept'))
const OrgAccount = lazy(() => import('@src/pages/org/account'))
const OrgRole = lazy(() => import('@src/pages/org/role'))
const OrgPermission = lazy(() => import('@src/pages/org/permission'))
const ConfigMenuSearch = lazy(() => import('@src/pages/config/menu/search'))
const ConfigCodeSearch = lazy(() => import('@src/pages/config/code/search'))
const ConfigNoticeSearch = lazy(() => import('@src/pages/config/notice/search'))
const ConfigScheduleSearch = lazy(() => import('@src/pages/config/schedule/search'))

const LogsSearch = lazy(() => import('@src/pages/logs/search'))

// 站内信
const SysMessage = lazy(() => import('@src/pages/sys/message'))

// 监控中心
const MonitorOnline = lazy(() => import('@src/pages/monitor/online'))
const MonitorServer = lazy(() => import('@src/pages/monitor/server'))

// 批处理日志
const ConfigScheduleLog = lazy(() => import('@src/pages/config/schedule/log'))

// AI 能力
const AiMcpServers = lazy(() => import('@src/pages/ai/mcp/servers'))
const AiLlmProviders = lazy(() => import('@src/pages/ai/llm/providers'))
const AiLlmPrompts = lazy(() => import('@src/pages/ai/llm/prompts'))
const AiTraining = lazy(() => import('@src/pages/ai/training'))
// H5 移动端训练场(独立路由,不挂 PC Layout 下,匿名访问)
const H5Training = lazy(() => import('@src/pages/h5/training'))

const RenderRouter: FC = () => {
	const { formatMessage: i18nMsg } = useIntl()
	const routeList: RouteObject[] = [
		{
			path: '/',
			element: (
				<WrapperRouteComponent titleId="" auth>
					<LayoutPage />
				</WrapperRouteComponent>
			),
			children: [
				{
					index: true, // 表示当访问 '/' 时匹配这个路由
					element: <Navigate to="/login" replace />
				},
				{
					path: 'dashboard/workbench',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.dashboard.workbench' })} auth>
							<DashboardWorkbench />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'dashboard/analysis',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.dashboard.analysis' })} auth>
							<DashboardAnalysis />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'form/basic',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.form.basic' })} auth>
							<FormBasic />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'form/advanced',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.form.advanced' })} auth>
							<FormAdvanced />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'form/editor',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.form.editor' })} auth>
							<FormEditor />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'list/search',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.list.search' })} auth>
							<ListSearch />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'result/success',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.result.success' })} auth>
							<ResultSuccess />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'result/failed',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.result.failed' })} auth>
							<ResultFailed />
						</WrapperRouteComponent>
					)
				},

				{
					path: 'abnormal/403',
					element: (
						<WrapperRouteComponent titleId="403" auth>
							<Abnormal403 />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'abnormal/404',
					element: (
						<WrapperRouteComponent titleId="404" auth>
							<Abnormal404 />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'abnormal/500',
					element: (
						<WrapperRouteComponent titleId="500" auth>
							<Abnormal500 />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'org/dept',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.org.dept' })} auth>
							<OrgDept />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'org/account',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.org.account' })} auth>
							<OrgAccount />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'org/role',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.org.role' })} auth>
							<OrgRole />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'org/permission',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.org.permission' })} auth>
							<OrgPermission />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'user/center',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.user.center' })} auth>
							<UserCenter />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'user/settings',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.user.settings' })} auth>
							<UserSettings />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'config/menu/search',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.config.search' })} auth>
							<ConfigMenuSearch />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'config/code/search',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.config.search' })} auth>
							<ConfigCodeSearch />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'config/notice/search',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.config.notice.search' })} auth>
							<ConfigNoticeSearch />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'config/schedule/search',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.config.schedule.search' })} auth>
							<ConfigScheduleSearch />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'config/schedule/log',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.config.schedule.log' })} auth>
							<ConfigScheduleLog />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'logs/search',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.logs.search' })} auth>
							<LogsSearch />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'monitor/online',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.monitor.online' })} auth>
							<MonitorOnline />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'monitor/server',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.monitor.server' })} auth>
							<MonitorServer />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'sys/message',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.message.title' })} auth>
							<SysMessage />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'ai/mcp/servers',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.ai.mcp.servers' })} auth>
							<AiMcpServers />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'ai/llm/providers',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.ai.llm.providers' })} auth>
							<AiLlmProviders />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'ai/llm/prompts',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.ai.llm.prompts' })} auth>
							<AiLlmPrompts />
						</WrapperRouteComponent>
					)
				},
				{
					path: 'ai/llm/training',
					element: (
						<WrapperRouteComponent titleId={i18nMsg({ id: 'app.menu.ai.training' })} auth>
							<AiTraining />
						</WrapperRouteComponent>
					)
				}
			]
		},
		{
			path: 'login',
			element: (
				<WrapperRouteWithOutLayoutComponent titleId={i18nMsg({ id: 'app.menu.login' })}>
					<LoginPage />
				</WrapperRouteWithOutLayoutComponent>
			)
		},
		{
			// H5 移动端训练场 —— 独立路由,不走 PC Layout,无登录态(走 /api/ai/llm/chat/stream 匿名调用)
			path: 'h5/training',
			element: (
				<WrapperRouteWithOutLayoutComponent titleId={i18nMsg({ id: 'app.h5.training' })}>
					<H5Training />
				</WrapperRouteWithOutLayoutComponent>
			)
		},
		{
			path: '*',
			element: (
				<WrapperRouteWithOutLayoutComponent titleId="404">
					<Empty title="找不到咯" description="这里什么也没有~" type="404" />
				</WrapperRouteWithOutLayoutComponent>
			)
		}
	]
	return useRoutes(routeList)
}

export default RenderRouter
